import { formaterNombre } from './geometrie.js';
/** Au plus deux traits : une courbe, ou les deux branches d'une hyperbole. */
export const TRAITS_MAX_ESQUISSE = 2;
/** Au plus tant de points par trait (le client simplifie avant d'envoyer). */
export const POINTS_MAX_TRAIT_ESQUISSE = 200;
/** Le pas de la grille du repère (une « case ») ; 1 par défaut. */
export function caseDuRepere(r) {
    const p = r.pas;
    if (typeof p === 'number' && p > 0)
        return p;
    if (typeof p === 'object' && p !== null) {
        const x = p.x;
        if (typeof x === 'number' && x > 0)
            return x;
    }
    return 1;
}
const nb = (v) => formaterNombre(v).replace(/^-/u, '−');
/** Arrondi à la demi-case : ce que l'œil lit d'un trait. */
const auDemi = (v, c) => Math.round((v * 2) / c) * (c / 2) + 0;
/* ─────────────────────────────── la réduction ─────────────────────────────── */
/** Un trait remis de gauche à droite ; `null` s'il revient sur ses pas de plus d'une demi-case. */
function deGaucheADroite(trait, c) {
    const pts = trait.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    if (pts.length < 2)
        return null;
    const premier = pts[0];
    const dernier = pts[pts.length - 1];
    const ordre = dernier.x >= premier.x ? pts : [...pts].reverse();
    /* Un retour en arrière de plus d'une demi-case n'est pas un tremblement : la courbe n'est pas celle d'une fonction. */
    let max = -Infinity;
    for (const p of ordre) {
        if (p.x < max - c / 2)
            return null;
        max = Math.max(max, p.x);
    }
    /* Les petits retours sont gommés : on garde le plus récent point de chaque abscisse. */
    const sortie = [];
    for (const p of ordre) {
        while (sortie.length > 0 && sortie[sortie.length - 1].x >= p.x)
            sortie.pop();
        sortie.push(p);
    }
    return sortie.length >= 2 ? sortie : null;
}
/** Le trait rééchantillonné à pas constant en x, par interpolation linéaire. */
function reechantillonner(trait, pas) {
    const x0 = trait[0].x;
    const x1 = trait[trait.length - 1].x;
    const n = Math.max(2, Math.floor((x1 - x0) / pas) + 1);
    const sortie = [];
    let j = 0;
    for (let i = 0; i < n; i += 1) {
        const x = i === n - 1 ? x1 : x0 + i * pas;
        while (j < trait.length - 2 && trait[j + 1].x < x)
            j += 1;
        const a = trait[j];
        const b = trait[j + 1];
        const t = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x);
        sortie.push({ x, y: a.y + Math.max(0, Math.min(1, t)) * (b.y - a.y) });
    }
    return sortie;
}
/**
 * Les morceaux MONOTONES d'un trait, avec une hystérésis : une variation de
 * moins de `seuil` ne renverse pas le sens (un tremblement de la main n'est
 * pas un sommet). Rend les points de bascule (les extremums) et les sens.
 */
function morceaux(pts, seuil) {
    const sens = [];
    const bascules = [];
    let courant = null;
    /* Le point extrême du morceau en cours (le plus haut s'il croît, le plus bas s'il décroît). */
    let extreme = pts[0];
    const ancre = pts[0];
    for (const p of pts) {
        if (courant === null) {
            if (p.y - ancre.y >= seuil)
                courant = 'croissante';
            else if (ancre.y - p.y >= seuil)
                courant = 'decroissante';
            else
                continue;
            sens.push(courant);
            extreme = p;
            continue;
        }
        if (courant === 'croissante') {
            if (p.y >= extreme.y)
                extreme = p;
            else if (extreme.y - p.y >= seuil) {
                bascules.push({ x: extreme.x, y: extreme.y, sorte: 'max' });
                courant = 'decroissante';
                sens.push(courant);
                extreme = p;
            }
        }
        else if (p.y <= extreme.y)
            extreme = p;
        else if (p.y - extreme.y >= seuil) {
            bascules.push({ x: extreme.x, y: extreme.y, sorte: 'min' });
            courant = 'croissante';
            sens.push(courant);
            extreme = p;
        }
    }
    return { sens, bascules };
}
/** Les zéros d'un trait : là où son signe change ; un extremum posé sur l'axe compte aussi. */
function zerosDe(pts, bascules, tol) {
    const zeros = [];
    for (let i = 1; i < pts.length; i += 1) {
        const a = pts[i - 1];
        const b = pts[i];
        if ((a.y < 0 && b.y >= 0) || (a.y > 0 && b.y <= 0)) {
            zeros.push(a.x + ((0 - a.y) * (b.x - a.x)) / (b.y - a.y));
        }
    }
    for (const e of bascules) {
        if (Math.abs(e.y) <= tol && !zeros.some((z) => Math.abs(z - e.x) <= tol))
            zeros.push(e.x);
    }
    return zeros.sort((u, v) => u - v);
}
/** Une asymptote horizontale à un bout : la queue (sur deux cases au moins) presque plate. */
function queuePlate(pts, c, aDroite) {
    const x0 = pts[0].x;
    const x1 = pts[pts.length - 1].x;
    const longueur = Math.max(2 * c, (x1 - x0) / 4);
    if (x1 - x0 < 3 * c)
        return null;
    const queue = pts.filter((p) => (aDroite ? p.x >= x1 - longueur : p.x <= x0 + longueur));
    if (queue.length < 3)
        return null;
    const ys = queue.map((p) => p.y);
    if (Math.max(...ys) - Math.min(...ys) > c / 2)
        return null;
    /* Plate, mais aussi plus plate que le reste : une droite horizontale n'est pas une asymptote. */
    const reste = pts.filter((p) => !queue.includes(p)).map((p) => p.y);
    if (reste.length === 0 || Math.max(...reste) - Math.min(...reste) < 2 * c)
        return null;
    const bout = aDroite ? queue[queue.length - 1] : queue[0];
    return bout.y;
}
/** Le bout d'un trait est-il RAIDE (une branche qui file vers une asymptote verticale) ? */
function boutRaide(pts, c, aDroite) {
    const n = pts.length;
    if (n < 3)
        return false;
    const a = aDroite ? pts[n - 1] : pts[0];
    const prises = pts.filter((p) => Math.abs(p.x - a.x) <= c / 2 && p !== a);
    const b = aDroite ? prises[0] : prises[prises.length - 1];
    if (b === undefined)
        return false;
    return Math.abs(a.y - b.y) >= 1.5 * c;
}
/**
 * LA RÉDUCTION d'une esquisse (un ou deux traits, en unités de figure) en
 * caractéristiques. Échec — et rien n'est jugé — si un trait est trop court ou
 * revient sur ses pas, ou s'il y a plus de deux traits.
 */
export function reduireEsquisse(traits, repere) {
    const c = caseDuRepere(repere);
    if (traits.length === 0)
        return { lue: false, raison: 'Aucun trait à lire.' };
    if (traits.length > TRAITS_MAX_ESQUISSE) {
        return {
            lue: false,
            raison: 'Esquisse ta courbe en un trait — deux au plus, de part et d’autre d’une asymptote.',
        };
    }
    const propres = [];
    for (const t of traits) {
        const p = deGaucheADroite(t, c);
        if (p === null) {
            return {
                lue: false,
                raison: 'On n’arrive pas à lire ton trait : une courbe de fonction avance de gauche à droite. Recommence-le d’un seul geste.',
            };
        }
        propres.push(p);
    }
    propres.sort((u, v) => u[0].x - v[0].x);
    const etendue = propres.reduce((s, p) => s + (p[p.length - 1].x - p[0].x), 0);
    if (etendue < 2 * c)
        return {
            lue: false,
            raison: 'Ton trait est trop court pour être lu. Esquisse la courbe sur plusieurs cases.',
        };
    const pas = c / 10;
    const seuil = c / 4;
    const tol = c / 2;
    const extremums = [];
    const zeros = [];
    const sens = [];
    const echantillons = propres.map((p) => reechantillonner(p, pas));
    for (const pts of echantillons) {
        const m = morceaux(pts, seuil);
        extremums.push(...m.bascules);
        zeros.push(...zerosDe(pts, m.bascules, tol / 2));
        /* Deux branches qui vont dans le même sens ne font qu'un sens à dire. */
        for (const s of m.sens)
            if (sens[sens.length - 1] !== s || echantillons.length > 1)
                sens.push(s);
    }
    const asymptotes = {};
    const gauche = echantillons[0];
    const droite = echantillons[echantillons.length - 1];
    const hg = queuePlate(gauche, c, false);
    const hd = queuePlate(droite, c, true);
    /* Les deux bouts filent vers la même horizontale, de part et d'autre (une hyperbole) : la
       moyenne. Un seul bout plat : le sien. */
    if (hg !== null && hd !== null && Math.abs(hg - hd) <= 2 * c)
        asymptotes.horizontale = (hg + hd) / 2;
    else if (hd !== null && hg === null)
        asymptotes.horizontale = hd;
    else if (hg !== null && hd === null)
        asymptotes.horizontale = hg;
    if (echantillons.length === 2) {
        const fin = gauche[gauche.length - 1].x;
        const debut = droite[0].x;
        if (debut - fin <= 2 * c && boutRaide(gauche, c, true) && boutRaide(droite, c, false)) {
            asymptotes.verticale = (fin + debut) / 2;
        }
    }
    return {
        lue: true,
        caracteristiques: {
            extremums,
            zeros: zeros.sort((u, v) => u - v),
            sens: echantillons.length > 1 ? sens : sens.filter((s, i) => i === 0 || sens[i - 1] !== s),
            asymptotes,
        },
    };
}
/* ─────────────────────────── la référence : une fonction ─────────────────────────── */
/**
 * Une FONCTION, échantillonnée dans la fenêtre, en traits : coupée là où elle
 * sort de la fenêtre (de plus d'une demi-hauteur) ou n'existe pas — c'est ce
 * qu'un élève tracerait sur cette grille.
 */
export function traitsDeLaFonction(f, repere, domaine = null) {
    const x0 = Math.max(repere.xMin, domaine?.[0] ?? repere.xMin);
    const x1 = Math.min(repere.xMax, domaine?.[1] ?? repere.xMax);
    const n = 600;
    const h = repere.yMax - repere.yMin;
    const traits = [];
    let courant = [];
    for (let i = 0; i <= n; i += 1) {
        const x = x0 + ((x1 - x0) * i) / n;
        const y = f(x);
        const dedans = Number.isFinite(y) && y >= repere.yMin - h / 2 && y <= repere.yMax + h / 2;
        if (dedans)
            courant.push({ x, y });
        else if (courant.length > 0) {
            traits.push(courant);
            courant = [];
        }
    }
    if (courant.length > 0)
        traits.push(courant);
    return traits.filter((t) => t.length >= 2);
}
const MOT_SENS = {
    croissante: 'croissante',
    decroissante: 'décroissante',
};
const pt = (x, y) => `(${nb(x)}\u00a0;\u00a0${nb(y)})`;
/**
 * LA COMPARAISON : la lecture de l'élève contre ce que la règle donne. Une
 * DEMI-CASE près sur un point remarquable (sommet, zéro, asymptote) ; le sens
 * de variation et l'ouverture, exactement. L'écart se dit sur UN paramètre :
 * « ton sommet est en (2 ; −3), celui de la règle en (−2 ; −3) ».
 */
export function jugerCaracteristiques(lue, attendues, repere) {
    const c = caseDuRepere(repere);
    const tol = c / 2 + 1e-9;
    const points = [];
    const seul = lue.extremums.length === 1 ? lue.extremums[0] : null;
    if (attendues.sommet !== undefined) {
        const s = attendues.sommet;
        const lu = seul === null ? null : { x: auDemi(seul.x, c), y: auDemi(seul.y, c) };
        const tenue = seul !== null && Math.abs(seul.x - s.x) <= tol && Math.abs(seul.y - s.y) <= tol;
        points.push({
            quoi: 'sommet',
            tenue,
            texte: tenue
                ? `Ton sommet est bien en ${pt(s.x, s.y)}.`
                : lu === null
                    ? `Ta courbe n’a pas UN sommet ; celui de la règle est en ${pt(s.x, s.y)}.`
                    : `Ton sommet est en ${pt(lu.x, lu.y)} ; celui de la règle est en ${pt(s.x, s.y)}.`,
        });
    }
    if (attendues.ouverture !== undefined) {
        const o = attendues.ouverture;
        const lu = seul === null ? null : seul.sorte === 'min' ? 'haut' : 'bas';
        const tenue = lu === o;
        points.push({
            quoi: 'ouverture',
            tenue,
            texte: tenue
                ? `Ta courbe est bien ouverte vers le ${o}.`
                : lu === null
                    ? `Ta courbe ne s’ouvre ni vers le haut ni vers le bas ; celle de la règle s’ouvre vers le ${o}.`
                    : `Ta courbe s’ouvre vers le ${lu} ; celle de la règle, vers le ${o}.`,
        });
    }
    if (attendues.zeros !== undefined) {
        const z = [...attendues.zeros].sort((u, v) => u - v);
        const tenue = z.length === lue.zeros.length &&
            z.every((x, i) => Math.abs(x - lue.zeros[i]) <= tol);
        const dire = (xs) => xs.length === 0 ? 'aucun zéro' : xs.map((x) => `x = ${nb(x)}`).join(' et ');
        points.push({
            quoi: 'zeros',
            tenue,
            texte: tenue
                ? `Ta courbe coupe bien l’axe des x en ${dire(z)}.`
                : `Ta courbe coupe l’axe des x en ${dire(lue.zeros.map((x) => auDemi(x, c)))} ; celle de la règle, en ${dire(z)}.`,
        });
    }
    if (attendues.sens !== undefined) {
        const a = attendues.sens;
        const tenue = a.length === lue.sens.length && a.every((s, i) => s === lue.sens[i]);
        const dire = (xs) => xs.length === 0 ? 'ni croissante ni décroissante' : xs.map((s) => MOT_SENS[s]).join(' puis ');
        points.push({
            quoi: 'sens',
            tenue,
            texte: tenue
                ? `Ta courbe est bien ${dire(a)}.`
                : `Ta courbe est ${dire(lue.sens)} ; celle de la règle est ${dire(a)}.`,
        });
    }
    for (const quoi of ['horizontale', 'verticale']) {
        const v = attendues.asymptotes?.[quoi];
        if (v === undefined)
            continue;
        const l = lue.asymptotes[quoi];
        const tenue = l !== undefined && Math.abs(l - v) <= tol;
        const eq = (w) => (quoi === 'horizontale' ? `y = ${nb(w)}` : `x = ${nb(w)}`);
        points.push({
            quoi: `asymptote_${quoi}`,
            tenue,
            texte: tenue
                ? `Ton asymptote ${quoi} est bien ${eq(v)}.`
                : l === undefined
                    ? `Ta courbe ne montre pas d’asymptote ${quoi} ; celle de la règle est ${eq(v)}.`
                    : `Ton asymptote ${quoi} est ${eq(auDemi(l, c))} ; celle de la règle est ${eq(v)}.`,
        });
    }
    return { tenue: points.every((p) => p.tenue), points };
}
/* ─────────────────────────────── dire ─────────────────────────────── */
/**
 * CE QU'ON A LU, en mots, à la demi-case comme l'œil lit — ce que l'élève
 * confirme avant la correction : « une courbe de sommet (2 ; −3), ouverte vers
 * le haut, qui coupe l'axe des x en x = −0,5 et x = 4,5 ».
 */
export function decrireCaracteristiques(lue, repere) {
    const c = caseDuRepere(repere);
    const ici = (e) => pt(auDemi(e.x, c), auDemi(e.y, c));
    const morceaux = [];
    if (lue.extremums.length === 1) {
        const e = lue.extremums[0];
        morceaux.push(`de sommet ${ici(e)}, ouverte vers le ${e.sorte === 'min' ? 'haut' : 'bas'}`);
    }
    else if (lue.extremums.length > 1) {
        morceaux.push(`qui a ${String(lue.extremums.length)} sommets, en ${lue.extremums.map(ici).join(' et ')}`);
    }
    else if (lue.sens.length > 0) {
        const memes = lue.sens.every((x) => x === lue.sens[0]);
        morceaux.push(lue.sens.length > 1 && memes
            ? `${MOT_SENS[lue.sens[0]]} sur chaque branche`
            : lue.sens.map((x) => MOT_SENS[x]).join(' puis '));
    }
    if (lue.zeros.length > 0) {
        morceaux.push(`qui coupe l’axe des x en ${lue.zeros.map((x) => `x = ${nb(auDemi(x, c))}`).join(' et ')}`);
    }
    const { horizontale: h, verticale: v } = lue.asymptotes;
    if (h !== undefined && v !== undefined) {
        morceaux.push(`d’asymptotes x = ${nb(auDemi(v, c))} et y = ${nb(auDemi(h, c))}`);
    }
    else if (h !== undefined) {
        morceaux.push(`d’asymptote horizontale y = ${nb(auDemi(h, c))}`);
    }
    else if (v !== undefined) {
        morceaux.push(`d’asymptote verticale x = ${nb(auDemi(v, c))}`);
    }
    return morceaux.length === 0
        ? 'une courbe sans sommet ni zéro'
        : `une courbe ${morceaux.join(', ')}`;
}
/**
 * Un trait de l'élève SIMPLIFIÉ avant d'être envoyé : au plus
 * `POINTS_MAX_TRAIT_ESQUISSE` points, gardés à un écart régulier, arrondis au
 * centième d'unité (le bruit flottant ne se lit pas).
 */
export function simplifierTrait(trait) {
    const propre = (v) => Math.round(v * 100) / 100;
    const pas = Math.max(1, Math.ceil(trait.length / POINTS_MAX_TRAIT_ESQUISSE));
    const sortie = [];
    trait.forEach((p, i) => {
        if (i % pas === 0 || i === trait.length - 1)
            sortie.push({ x: propre(p.x), y: propre(p.y) });
    });
    return sortie.slice(0, POINTS_MAX_TRAIT_ESQUISSE);
}
/* ─────────────────────────────── le trait par les points ─────────────────────────────── */
/**
 * LE TRAIT QUI PASSE PAR LES POINTS, de gauche à droite (L6, F8) : une cubique
 * MONOTONE par morceaux (Fritsch–Carlson). Elle ne dépasse jamais un point :
 * entre deux points, elle monte ou descend comme eux — aucun sommet n'y naît
 * que l'élève n'a pas posé. C'est le trait qui « se dessine » quand on pose
 * les points d'une table, et celui d'une esquisse posée au clavier.
 * `parIntervalle` : le nombre de points calculés entre deux points posés.
 */
export function courbeParLesPoints(points, parIntervalle = 12) {
    const p = [...points]
        .filter((q) => Number.isFinite(q.x) && Number.isFinite(q.y))
        .sort((a, b) => a.x - b.x)
        .filter((q, i, t) => i === 0 || q.x - t[i - 1].x > 1e-9);
    const n = p.length;
    if (n < 2)
        return p;
    const h = [];
    const d = [];
    for (let i = 0; i < n - 1; i += 1) {
        const a = p[i];
        const b = p[i + 1];
        h.push(b.x - a.x);
        d.push((b.y - a.y) / (b.x - a.x));
    }
    /* Les pentes aux points : la moyenne des pentes voisines, nulle à un extremum. */
    const m = [d[0]];
    for (let i = 1; i < n - 1; i += 1) {
        const g = d[i - 1];
        const dr = d[i];
        m.push(g * dr <= 0 ? 0 : (g + dr) / 2);
    }
    m.push(d[n - 2]);
    /* Fritsch–Carlson : on rabat les pentes qui feraient dépasser. */
    for (let i = 0; i < n - 1; i += 1) {
        const di = d[i];
        if (di === 0) {
            m[i] = 0;
            m[i + 1] = 0;
            continue;
        }
        const a = m[i] / di;
        const b = m[i + 1] / di;
        const s = a * a + b * b;
        if (s > 9) {
            const t = 3 / Math.sqrt(s);
            m[i] = t * a * di;
            m[i + 1] = t * b * di;
        }
    }
    const sortie = [];
    for (let i = 0; i < n - 1; i += 1) {
        const a = p[i];
        const b = p[i + 1];
        const hi = h[i];
        for (let k = 0; k < parIntervalle; k += 1) {
            const t = k / parIntervalle;
            const h00 = 2 * t ** 3 - 3 * t ** 2 + 1;
            const h10 = t ** 3 - 2 * t ** 2 + t;
            const h01 = -2 * t ** 3 + 3 * t ** 2;
            const h11 = t ** 3 - t ** 2;
            sortie.push({
                x: a.x + t * hi,
                y: h00 * a.y + h10 * hi * m[i] + h01 * b.y + h11 * hi * m[i + 1],
            });
        }
    }
    sortie.push(p[n - 1]);
    return sortie;
}
//# sourceMappingURL=esquisse.js.map